import { publicPath } from './model/config';
import { idOf, objectToParamsPart } from './util/util';
import { Event } from './model/event';
import { Company } from './model/company';
import { Venue } from './model/venue';
import { Tour } from './model/tour';
import { User } from './model/user';
/*import { User } from './model/user';*/
import { Product } from './model/product';
import { Order } from './model/order';
import { Picklist } from './model/picklist';
import { Delivery } from './model/delivery';
import { Category } from './model/category';
import { Shop } from './model/shop';
import { News } from './model/news';
import { Contract } from './model/contract';
import { api } from './model/store';
import { VoucherCampaign } from './generated/graphql';

function parameterString(params: Record<string, string>): string {
  const searchParams = new URLSearchParams();
  Object.keys(params).forEach((key) => {
    searchParams.set(key, params[key]);
  });
  return '?' + searchParams.toString();
}

export function dashboardPath(): string {
  return `${publicPath}/`;
}
export function styleguidePath(): string {
  return `${publicPath}/styleguide`;
}
export function queuePath(): string {
  return `${publicPath}/queue`;
}

export function resetPasswordPath(): string {
  return `${publicPath}/password-reset`;
}

export function signUpPath(token?: string): string {
  return `${publicPath}/signup/${token}`;
}
export function invitesPath(token: string): string {
  return `${publicPath}/invites/${token}`;
}

export function eventsPath(): string {
  return `${publicPath}/events`;
}
export function eventPath(event: string | Event): string {
  return `${publicPath}/events/${idOf(event)}`;
}
export function eventPrintTicketPath(event: string | Event): string {
  return `${eventPath(event)}/ticket/print`;
}

export function companiesPath(): string {
  return `${publicPath}/companies`;
}
export function companyPath(company: string | Company): string {
  return `${publicPath}/companies/${idOf(company)}`;
}
export function companyPayoutsPath(company: string | Company): string {
  return urlForPath(`/connect2/companies/${idOf(company)}/event-payouts`);
}

export function venuesPath(): string {
  return `${publicPath}/venues`;
}
export function venuePath(venue: string | Venue): string {
  return `${publicPath}/venues/${idOf(venue)}`;
}

export function venueLayoutPath(venueLayout: string | Venue): string {
  return `${publicPath}/venue-layouts/${idOf(venueLayout)}`;
}

export function eventTurnoversPath(): string {
  return `${publicPath}/event-turnovers`;
}

export function toursPath(): string {
  return `${publicPath}/tours`;
}
export function tourPath(tour: string | Tour): string {
  return `${publicPath}/tours/${idOf(tour, true)}`;
}

export function usersPath(): string {
  return `${publicPath}/users`;
}
export function userPath(user: string | User): string {
  return `${publicPath}/user/${idOf(user)}`;
}

export function productsPath(): string {
  return `${publicPath}/products`;
}
export function productPath(product: string | Product): string {
  return `${publicPath}/products/${idOf(product)}`;
}

export function merchStatisticsPath(): string {
  return `${publicPath}/merch-statistics`;
}

export function categoriesPath(): string {
  return `${publicPath}/categories`;
}
export function categoryPath(category: string | Category): string {
  return `${publicPath}/categories/${idOf(category, true)}`;
}

export function newsPath(): string {
  return `${publicPath}/news`;
}
export function newsDetailsPath(newsItem: string | News): string {
  return `${publicPath}/news/${idOf(newsItem)}`;
}

export function shopsPath(): string {
  return `${publicPath}/shops`;
}
export function shopPath(shop: string | Shop): string {
  return `${publicPath}/shops/${idOf(shop)}`;
}

export function ordersPath(
  filters: {
    productId?: string;
    productVariantId?: string;
    venueSectionId?: string;
    itemStatus?: string;
  } = {}
): string {
  return `${publicPath}/orders${objectToParamsPart(filters)}`;
}

export function orderPath(order: string | Order): string {
  return `${publicPath}/orders/${idOf(order)}`;
}

export function contractsPath(): string {
  return `${publicPath}/contracts`;
}
export function contractPath(contract: string | Contract): string {
  return `${publicPath}/contracts/${idOf(contract)}`;
}

export function picklistsPath(): string {
  return `${publicPath}/picklists`;
}
export function picklistPath(picklist: string | Picklist): string {
  return `${publicPath}/picklist/${idOf(picklist)}`;
}

export function invoicePath(order: string | Order, invoice: string): string {
  return `${publicPath}/orders/${idOf(order)}/invoices/${invoice}`;
}

export function deliveryPath(order: string | Order, delivery: string): string {
  return `${publicPath}/orders/${idOf(order)}/delivery-notes/${delivery}`;
}

export function fulfillmentPath(): string {
  return `${publicPath}/fulfillment`;
}
export function fulfillmentDetailsPath(invoice: string | Delivery): string {
  return `${publicPath}/fulfillment/${idOf(invoice)}`;
}

export function backPaymentsPath(): string {
  return `${publicPath}/back-payments/`;
}

export function bookKeepingExportPath(): string {
  return `${publicPath}/bookkeeping`;
}

export function settingsPath(): string {
  return `${publicPath}/settings`;
}

export function assetPath(id: string, extension?: string): string {
  const extensionPart = extension ? `.${extension}` : null;
  return `${api.baseUrl}/assets/${id}${extensionPart}`;
}

export function ticketScannerPath({ eventId, scannerName }: { eventId: string; scannerName: string }): string {
  return `${publicPath}/ticket-scanner${parameterString({ event_id: eventId, scanner_name: scannerName })}`;
}

export function urlForPath(path: string): string {
  const location = document.location;

  return `${location.protocol}//${location.host}${path}`;
}

export function scannerPath(): string {
  return `${publicPath}/ticket-scanner`;
}

export function chartsPath(): string {
  return `${publicPath}/charts`;
}

export function genericProductPath(product?: { id?: string; __typename?: string }): string {
  if (!product) {
    return '';
  }
  if (!product.id) {
    return '';
  }
  switch (product.__typename) {
    case 'Event':
      return eventPath(product.id);
    default:
      return productPath(product.id);
  }
}

// Voucher Campaigns
export function voucherCampaignsPath(): string {
  return `${publicPath}/voucher-campaigns`;
}

// TODO#2359: change param to VoucherCampaign type
export function voucherCampaignPath(voucherCampaign: string | VoucherCampaign): string {
  return `${publicPath}/voucher-campaigns/${idOf(voucherCampaign)}`;
}

export function warehousePath(): string {
  return urlForPath('/connect2/warehouse');
}
